import React, { Suspense, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AdaptiveCarousel, AdaptiveCarouselSlide, Box, Link } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { Image } from '../domain/Image';
import { Slide } from '../domain/Slide';
import { SlideArray } from '../domain/SlideArray';
import { Video } from '../domain/Video';
import { GalleryId } from '../libs/constants';
import { scrollGalleryToTargetSlide } from '../libs/scrollGalleryToTargetSlide';
import { pauseVideo } from '../libs/videoActions';

import { GalleryImage } from './GalleryImage';
import { GalleryVideo } from './GalleryVideo';
import { Thumbnails } from './Thumbnails';
import { SwanZoomableGalleryImage } from './zoom-swan/SwanZoomableGalleryImage';

const SwanZoomLightbox = React.lazy(() =>
  import('./zoom-swan/SwanZoomLightbox').then((m) => ({ default: m.SwanZoomLightbox }))
);

export interface GoToSlide {
  slideNumber: number;
}
interface GalleryProps {
  mediaArray: SlideArray;
  goToSlide?: GoToSlide;
  tenant: string;
  mpvId: string;
  productKey: string;
  disableZoom?: boolean;
  productPageUrl?: string;
}
export const Gallery: React.FC<GalleryProps> = (props) => {
  const { t } = useLocalizations();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlide, setLightboxSlide] = useState(0);

  const [currentSlide, setCurrentSlide] = useState<number>(
    props.goToSlide ? props.goToSlide.slideNumber : 1
  );
  const carouselRef = useRef<HTMLElement>(null);

  const openLightbox = useCallback((slideIndex: number) => {
    setLightboxSlide(slideIndex);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleSlideChange = (firstSlide: number): void => {
    const unselectedSlide = props.mediaArray.getSlideNumber(currentSlide);
    if (unselectedSlide?.isVideo) {
      pauseVideo(unselectedSlide.galleryId);
    }
    setCurrentSlide(firstSlide);
  };

  const lightboxImages = useMemo(
    () => props.mediaArray.imagesWithLocalizedAlt(t),
    [props.mediaArray, t]
  );

  const renderCarouselSlides = (): React.ReactNode[] => {
    return props.mediaArray.all.map((slide: Slide, index: number) => {
      if (slide.isVideo) {
        return (
          <AdaptiveCarouselSlide key={slide.galleryId} alignSelf="stretch">
            <GalleryVideo
              video={slide.media as Video}
              playerId={slide.galleryId}
              tenant={props.tenant}
            />
          </AdaptiveCarouselSlide>
        );
      }

      const image = slide.media as Image;
      const alt = slide.getAlt(t);

      const galleryImage = props.disableZoom ? (
        <GalleryImage image={image} alt={alt} />
      ) : (
        <SwanZoomableGalleryImage
          image={image}
          alt={alt}
          onExpandClick={() => openLightbox(index)}
        />
      );

      return (
        <AdaptiveCarouselSlide key={slide.galleryId}>
          {props.productPageUrl && props.disableZoom ? (
            <Link
              href={props.productPageUrl}
              skin="unstyled"
              aria-label={t('product-page-link-label')}
            >
              {galleryImage}
            </Link>
          ) : (
            galleryImage
          )}
        </AdaptiveCarouselSlide>
      );
    });
  };

  useLayoutEffect(() => {
    if (!props.goToSlide || props.goToSlide.slideNumber === currentSlide) {
      return;
    }

    scrollGalleryToTargetSlide(carouselRef.current, props.goToSlide.slideNumber);
  }, [props.goToSlide]);

  const baseCarouselProps: React.ComponentProps<typeof AdaptiveCarousel> = {
    initialSlide: props.goToSlide?.slideNumber,
    key: props.mediaArray.galleryKey,
    'aria-label': t('image-container-label'),
    onSlideChange: handleSlideChange,
    slidesToShow: 1,
    slidePicker: (
      <Thumbnails mediaArray={props.mediaArray} mpvId={props.mpvId} productKey={props.productKey} />
    ),
  };

  return (
    <Box id={GalleryId}>
      <AdaptiveCarousel ref={carouselRef} {...baseCarouselProps}>
        {renderCarouselSlides()}
      </AdaptiveCarousel>
      {!props.disableZoom && (
        <Suspense fallback={null}>
          <SwanZoomLightbox
            images={lightboxImages}
            initialSlide={lightboxSlide}
            isOpen={lightboxOpen}
            onRequestDismiss={closeLightbox}
          />
        </Suspense>
      )}
    </Box>
  );
};
